import React from "react";

import { faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faPencil } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, Radio } from "@performyard/py-components";
import type { SideNavTheme } from "@performyard/service-contracts";
import { SIDE_NAV_DEFAULT_THEME_ID } from "@performyard/utils-isomorphic";

type ThemeRadioCellProps = {
	theme: SideNavTheme;
	isDefaultPreset: boolean;
	isSelected: boolean;
	onSelect: (themeId: string) => void;
	onEdit?: (themeId: string) => void;
	onDelete?: (themeId: string) => void;
	disabled?: boolean;
};

export const ThemeRadioCell = ({
	theme,
	isDefaultPreset,
	isSelected,
	onSelect,
	onEdit,
	onDelete,
	disabled = false,
}: ThemeRadioCellProps) => (
	<div
		className={`box-border flex h-11 w-[324px] items-center gap-2 rounded-md border border-solid bg-neutral-0 p-2 text-left text-paragraph-3 text-neutral-9 ${
			isSelected ? "border-primary-5" : "border-neutral-2"
		} ${disabled ? "opacity-60" : ""}`}
	>
		<label
			className={`flex min-w-0 flex-1 items-center gap-2 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
		>
			<Radio
				name="sidenav-theme"
				value={theme.id}
				checked={isSelected}
				onChange={() => onSelect(theme.id)}
				aria-label={`Select ${theme.name}`}
				data-testid={`sidenav-theme-radio-${theme.id}`}
				disabled={disabled}
			/>
			<span
				aria-hidden="true"
				className="size-7 shrink-0 rounded-sm border border-solid border-neutral-2"
				style={{ backgroundColor: theme.backgroundColor }}
			/>
			<span className="min-w-0 flex-1 truncate">
				{theme.name}
				{isDefaultPreset && theme.id === SIDE_NAV_DEFAULT_THEME_ID ? " (Default)" : ""}
			</span>
		</label>
		{!isDefaultPreset && onEdit ? (
			<span className="flex shrink-0 items-center gap-1">
				<ButtonIcon
					icon={faPencil}
					variant="standard"
					color="neutral"
					shade={5}
					size={16}
					aria-label={`Edit ${theme.name}`}
					data-testid={`sidenav-theme-edit-${theme.id}`}
					onClick={() => onEdit(theme.id)}
					disabled={disabled}
				/>
				<ButtonIcon
					icon={faTrashCan}
					variant="standard"
					color="neutral"
					shade={5}
					size={16}
					aria-label={`Delete ${theme.name}`}
					onClick={() => onDelete?.(theme.id)}
					disabled={disabled || !onDelete}
				/>
			</span>
		) : null}
	</div>
);
